<template>
  <DrawerVue :refreshDisplay="refreshDisplay" id="billDrawer">
    <v-container>
      <v-row class="mb-4" align="center">
        <v-col cols="auto">
          <h2 v-if="billData">
            {{ $t("basics.edit", { type: $t("basics.bill") }) }}
          </h2>
          <h2 v-else>
            {{ $t("basics.new", { type: $t("basics.bill") }) }}
          </h2>
        </v-col>
      </v-row>

      <v-form ref="billEditForm">
        <v-row v-if="billData">
          <v-col>
            <v-text-field :readonly="true" :label="$t('basics.billingNumber')" v-model="billingNumber"
              variant="outlined" flat>
            </v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="pb-0">
            <v-combobox :readonly="true" :label="$t('basics.receiver')" :items="availRecievers" v-model="receiver"
              autocomplete="off" variant="outlined" flat return-object :item-title="(item) => item.full_name"
              @update:search="fetchReceivers" no-filter :item-value="(item) => item.full_name"
              :rules="[requiredRule, validObject]">
            </v-combobox>
          </v-col>
        </v-row>

        <v-row v-if="receiver && receiver.comment_internal">
          <v-col class="pb-0">
            {{ $t("basics.comment") }} {{ $t("basics.receiver") }}: „<i>{{ receiver.comment_internal }}</i>“
          </v-col>
        </v-row>
        <!-- <v-row>
          <v-col class="py-0">
            <v-checkbox class="hideDetails" disabled v-model="vatExempt" :label="$t('basics.vatExempt')">
            </v-checkbox>
          </v-col>
        </v-row> -->
        <v-row>
          <v-col class="py-0">
            <v-checkbox class="hideDetails" v-model="privateInsurance" :label="$t('basics.privateInsurance')">
            </v-checkbox>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-combobox :readonly="readonly" :label="$t('basics.diagnoses')" chips closable-chips
              :items="availDiagnoses" v-model="diagnoses" variant="outlined" flat :multiple="true" return-object
              autocomplete="off" @update:search="fetchDiagnoses" no-filter
              :item-title="(item) => item.number + ' ' + item.name"
              :item-value="(item) => item.number + ' ' + item.name" :rules="[validObjects]">
              <template v-slot:chip="{ props, item }">
                <v-chip v-bind="props" label>
                  {{ item.raw.number }}
                </v-chip>
              </template>
              <!-- TODO: needs to be added to all comboboxes -->
              <!-- <template v-slot:append-item="">
                <div class="ml-6">
                  <v-chip style="opacity: 0.6">+{{ diagnosisCount - availDiagnoses.length }}</v-chip>
                </div>
              </template> -->
            </v-combobox>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-menu v-model="dueDatePicker" :close-on-content-click="false" transition="v-scale-transition"
              min-width="auto" :disabled="readonly">
              <template v-slot:activator="{ props }">
                <v-text-field v-bind="props" variant="outlined" flat :model-value="getReadableDate(dueDate)" readonly
                  :label="$t('basics.dueDate') + '*'" append-inner-icon="mdi-calendar"
                  :rules="[requiredRule]"></v-text-field></template>
              <v-date-picker v-model="dueDate" v-if="dueDatePicker" color="rgb(var(--v-theme-primary))"
                :show-week="true" hide-header @update:modelValue="dueDatePicker = false"></v-date-picker>
            </v-menu>
          </v-col>
        </v-row>

        <v-row>
          <v-col class="py-0">
            <b>{{ $t("messages.appointmentsInThisBill") }}</b>
          </v-col>
        </v-row>

        <v-row v-for="(utt, uttIndex) in utts">
          <v-col>
            <v-card flat class="light-outline">
              <v-container fluid>
                <v-row>
                  <v-col cols="auto" class="pr-0" @click="removePatient(uttIndex)">
                    <v-tooltip :text="$t('basics.delete')">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" color="primary" style="margin-top: -2px;">mdi-trash-can</v-icon>
                      </template>
                    </v-tooltip>
                  </v-col>
                  <v-col cols="auto" class="pr-0" @click="gotoCalendar(utt)"
                    @mousedown.middle="gotoCalendar(utt, true)">
                    <v-tooltip :text="$t('basics.goto', { type: $t('routes.calendar') })">
                      <template v-slot:activator="{ props }">
                        <v-icon v-bind="props" color="primary"
                          style="margin-top: -2px;">mdi-calendar-arrow-right</v-icon>
                      </template>
                    </v-tooltip>
                  </v-col>
                  <v-col class="pb-0">
                    {{ utt.time_slot.product.name }} - {{ utt.time_slot.display_date }}
                    <span v-if="utt.user.id != receiver.id">{{ utt.user.full_name }}</span><br>
                  </v-col>
                  <v-col cols="4" class="pb-0">
                    <v-text-field v-if="!privateInsurance" density="compact" append-inner-icon="mdi-currency-eur"
                      v-model="utt.price" variant="outlined" flat>
                    </v-text-field>
                  </v-col>
                </v-row>
                <v-row>
                  <v-col class="pt-0">
                    <v-combobox :readonly="readonly" chips closable-chips :label="$t('routes.services')"
                      class="hideDetails" :items="availServices" v-model="utt.services"
                      :rules="privateInsurance ? [validObjects, requiredArrayRule] : [validObjects]" variant="outlined"
                      flat :multiple="true" return-object @update:search="fetchServices" no-filter autocomplete="off"
                      :item-title="(item) => (item.marked ? '&#127988; ' : '') + item.number + ' ' + item.name"
                      :item-value="(item) => item.number + ' ' + item.name">

                      <template v-slot:chip="{ props, item }">
                        <v-chip v-bind="props" label>
                          {{ item.raw.number }}
                        </v-chip>
                      </template>
                    </v-combobox>
                  </v-col>
                </v-row>

                <v-row v-if="utt.user && utt.user.comment_internal">
                  <v-col>
                    {{ $t("basics.comment") }} {{ $t("basics.patient") }}: „<i>{{ utt.user.comment_internal }}</i>“
                  </v-col>
                </v-row>
              </v-container>
            </v-card>
          </v-col>
        </v-row>

        <v-row>
          <v-col>
            <v-combobox :readonly="readonly" :label="$t('messages.additionalAppointments')" class="hideDetails"
              :items="computedAvailAppointments" :rules="[validObject]" variant="outlined" flat :multiple="true"
              return-object @update:search="fetchAppointments" no-filter autocomplete="off"
              :item-title="(item) => item && item.display_date ? item.display_date : 'etst'"
              :item-value="(item) => item && item.display_date ? item.display_date : 'etst'">

              <template v-slot:item="{ props, item }">
                <div class="pa-4">
                  <div v-if="item.raw.time_slot" @click="addPatient(item.raw)">
                    <div class="hoverable"> {{ item.raw.time_slot.product?.name }} - {{
                      item.raw.time_slot.display_date }}</div>
                    <div class="ml-4 hoverable">
                      {{ item.raw.user.full_name }}
                    </div>
                  </div>
                  <div v-else>
                    <div class="hoverable" @click="addAppointment(item.raw)"> {{ item.raw.product?.name }} - {{
                      item.raw.display_date }}</div>
                    <div class="ml-4 hoverable" @click="addPatient(p)" v-for="p in item.raw.patients">
                      {{ p.user.full_name }}
                    </div>
                  </div>
                  <v-divider class="mt-2" />
                </div>
              </template>
            </v-combobox>
          </v-col>
        </v-row>

        <v-row>
          <v-col class="mt-4">
            <v-select :readonly="readonly" :label="$t('basics.status')" :items="availStatuses" v-model="status"
              variant="outlined" flat @update:model-value="scrollToBottom" return-object
              :item-title="(item) => $t('constants.billing_status_' + item.value)"
              :item-value="(item) => $t('constants.billing_status_' + item.value)">
            </v-select>
          </v-col>
        </v-row>

        <v-row v-if="showStatusChangeHint">
          <v-col style="color: rgb(var(--v-theme-error))">
            {{ $t("messages.showStatusChangeHint") }}
          </v-col>
        </v-row>

        <v-row v-if="showStatusChangeHint">
          <v-col>
            <v-checkbox class="hideDetails" v-model="sendToReceiver" :label="$t('messages.sendMaiToReceiver')">
            </v-checkbox>
          </v-col>
        </v-row>

        <v-row v-if="showStatusChangeHint">
          <v-col>
            <v-checkbox class="hideDetails" @update:model-value="scrollToBottom" v-model="confirmedStatusChange"
              :label="$t('messages.confirmStatusChange')">
            </v-checkbox>
          </v-col>
        </v-row>

        <v-row v-if="!readonly && (!showStatusChangeHint || confirmedStatusChange)" class="mt-12">
          <v-col cols="12">
            <v-btn large block flat class="primaryButton" @click="confirm()" :loading="loading" ref="confirmButton"
              style="width: 100%">
              <span>{{ $t("basics.confirm") }}</span>
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </v-container>
  </DrawerVue>
</template>

<script>
export default {
  name: "billEditDrawer",
  props: ["billData", "refreshDisplay", "readonly"],
  emits: ["close"],

  data() {
    return {
      receiver: undefined,
      diagnoses: [],

      privateInsurance: false,
      vatExempt: true,

      billingNumber: undefined,
      status: undefined,

      dueDate: undefined,
      dueDatePicker: false,

      sendToReceiver: false,

      utts: undefined,
      uttsRemoved: [],

      confirmedStatusChange: false,

      availRecievers: undefined,
      availDiagnoses: undefined,
      availStatuses: undefined,
      availServices: undefined,
      availAppointments: undefined,

      diagnosisCount: 0,

      loading: false,

      requiredRule: (value) => !!value || this.$i18n.t("messages.required"),
      requiredArrayRule: (value) => value && value.length > 0 || this.$i18n.t("messages.required"),
      max256: (value) =>
        !value || value.length <= 256 || this.$i18n.t("messages.tooLong", { count: 256 }),
      validObjects: (value) =>
        !value ||
        this.checkArrayOfObjects(value) ||
        this.$i18n.t("messages.validSelectionrequired"),
      validObject: (value) =>
        !value ||
        typeof value == "object" ||
        this.$i18n.t("messages.validSelectionrequired"),
    };
  },

  computed: {
    showStatusChangeHint() {
      if (this.billData && this.status) {
        if (this.billData.display_status == "created" && this.status.value != "created") {
          return true
        }
      }

      return false
    },
    computedAvailAppointments() {
      return this.uttsRemoved.concat(this.availAppointments)
    }
  },

  watch: {
    refreshDisplay() {
      this.setValues();
    },
  },

  methods: {
    getReadableDate(date) {
      if (date) return this.$moment(date).format("DD.MM.YYYY");
      else return undefined;
    },
    scrollToBottom() {
      setTimeout(() => {
        const drawer = document.getElementById("billDrawer");
        if (!drawer) return;

        const scrollContainer = drawer.querySelector(".v-navigation-drawer__content");
        if (!scrollContainer) return;

        scrollContainer.scrollTo({
          top: scrollContainer.scrollHeight,
          behavior: "smooth",
        });
      }, 100);
    },
    isNotInUtts(utts) {
      for (let i = 0; i < this.utts.length; i++) {
        if (this.utts[i].id == utts.id) {
          return false
        }
      }

      return true
    },
    addAppointment(uts) {
      for (let i = 0; i < uts.patients.length; i++) {
        if (this.isNotInUtts(uts.patients[i])) {
          uts.patients[i].added = true
          this.utts.push(uts.patients[i])
        }
      }
    },
    addPatient(utts) {
      if (this.isNotInUtts(utts)) {
        this.utts.push(utts)
      }

      let found = false
      for (let i = 0; i < this.uttsRemoved.length; i++) {
        if (this.uttsRemoved[i].id == utts.id) {
          this.uttsRemoved.splice(i, 1)
          found = true
          break
        }
      }

      if (!found) {
        utts.added = true
      }
    },
    removePatient(uttsIndex) {
      if (this.utts[uttsIndex].added) {
        this.utts.splice(uttsIndex, 1)
      }
      else {
        this.uttsRemoved.push(this.utts.splice(uttsIndex, 1)[0])
      }
    },
    gotoCalendar(utts, newTab) {
      // if(newTab){
      window.open("/backoffice/calendar/own?date=" + utts.time_slot.date, "_blank")
      // }
      // else{
      //   this.$router.push("/backoffice/calendar/own?date=" + utts.time_slot.date)
      // }
    },
    checkArrayOfObjects(array) {
      for (let i = 0; i < array.length; i++) {
        if (typeof array[i] != "object") {
          return false
        }
      }

      return true
    },
    async confirm() {
      const { valid } = await this.$refs.billEditForm.validate();

      if (valid) {
        let diagnoses = this.diagnoses.map(d => d.id);
        let uttsRemoved = this.uttsRemoved.map(d => d.id);

        this.loading = true

        let formData = {
          diagnoses: diagnoses,
          private_insurance: this.privateInsurance,
          vat_exempt: this.vatExempt,
          status: this.status.key,

          due_date: this.$moment(this.dueDate).format("YYYY-MM-DD"),

          utts: JSON.stringify(this.utts),
          uttsRemoved: uttsRemoved,
          send_mail: this.sendToReceiver
        };



        if (this.billData) {
          formData.id = this.billData.id;
          this.patchData(formData);
        }
        else {
          window.alert("TODO")
          return
          this.postData(formData);
        }
      }
    },
    postData(formData) {
      this.axios
        .post(this.axios.defaults.baseURL + "media/bill/post/", formData)
        .then((resp) => {
          this.$emit("close", 1);
          this.loading = false
        })
        .catch((e) => {
          console.log(e);
          this.loading = false
          window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }));
        });
    },

    patchData(formData) {
      this.axios
        .patch(
          this.axios.defaults.baseURL + "user/bills/patch/",
          formData
        )
        .then((resp) => {
          this.$emit("close", 1);
          this.loading = false
        })
        .catch((e) => {
          console.log(e);
          this.loading = false
          window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }));
        });
    },
    setValues() {
      this.loading = false
      if (this.billData) {
        this.receiver = this.billData.receiver
        this.diagnoses = JSON.parse(JSON.stringify(this.billData.diagnoses))

        this.privateInsurance = this.billData.private_insurance
        this.vatExempt = this.billData.vat_exempt

        this.billingNumber = this.billData.billing_number

        this.utts = JSON.parse(JSON.stringify(this.billData.utts))

        if (this.billData.status !== undefined) {
          for (let i = 0; i < this.availStatuses.length; i++) {
            if (this.billData.status == this.availStatuses[i].key) {
              this.status = this.availStatuses[i]
            }
          }
        }
        else {
          this.status = this.availStatuses[0]
        }

        this.dueDate = this.billData.due_date
      } else {
        this.receiver = undefined
        this.diagnoses = []

        this.privateInsurance = false
        this.vatExempt = true

        this.billingNumber = undefined

        this.status = this.availStatuses[0]

        this.dueDate = undefined

        this.utts = undefined
      }
    },
    fetchData() {
      this.fetchStatusChoices().then(() => {
        this.fetchReceivers().then(() => {
          this.fetchDiagnoses().then(() => {
            this.fetchServices().then(() => {
              this.fetchAppointments()
            })
          })
        })
      })
    },
    fetchStatusChoices() {
      return new Promise((resolve, reject) => {
        this.axios.get(this.axios.defaults.baseURL + "user/bills/statuses/")
          .then((resp) => {
            this.availStatuses = resp.data

            resolve()
          })
          .catch((e) => {
            console.log(e)
            window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }))
            reject()
          })
      })
    },
    fetchReceivers(searchQ) {
      let query = ""
      if (searchQ) {
        query += "?query=" + searchQ
      }

      return new Promise((resolve, reject) => {
        this.axios.get(this.axios.defaults.baseURL + "user/users/" + query)
          .then((resp) => {
            this.availRecievers = resp.data.results

            resolve()
          })
          .catch((e) => {
            console.log(e)
            window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }))
            reject()
          })
      })
    },
    fetchDiagnoses(searchQ) {
      let query = "?direction=up"
      if (searchQ) {
        query += "&query=" + searchQ
      }

      return new Promise((resolve, reject) => {
        this.axios.get(this.axios.defaults.baseURL + "media/diagnosis/" + query)
          .then((resp) => {
            this.availDiagnoses = resp.data.results
            this.diagnosisCount = resp.data.count

            resolve()
          })
          .catch((e) => {
            console.log(e)
            window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }))
            reject()
          })
      })
    },
    fetchServices(searchQ) {
      let query = ""
      if (searchQ) {
        query += "?query=" + searchQ
      }

      return new Promise((resolve, reject) => {
        this.axios.get(this.axios.defaults.baseURL + "media/services/" + query)
          .then((resp) => {
            this.availServices = resp.data.results

            resolve()
          })
          .catch((e) => {
            console.log(e)
            window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }))
            reject()
          })
      })
    },
    fetchAppointments(searchQ) {
      let query = "?unbilled=1"
      if (searchQ) {
        query += "&query=" + searchQ
      }

      return new Promise((resolve, reject) => {
        this.axios.get(this.axios.defaults.baseURL + "calendar/timeslots/" + query)
          .then((resp) => {
            this.availAppointments = resp.data.results

            resolve()
          })
          .catch((e) => {
            console.log(e)
            window.dispatchEvent(new CustomEvent("showErrorDisplay", { detail: e }))
            reject()
          })
      })
    },
  },

  mounted() {
    this.fetchData()
  },
};
</script>
